/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import docApi from "../api";
import * as stores from "stores";
import { logCNPDFComponentLogs } from "logging";
import { getSharedFileUserType } from "logging/logToServer";
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { getCNPDFSwitchBeginTime, getPDFToCNPDFSwitchTime } from "utils/performance";
import { cnpdfComponentFetchTime } from "../constants/enums";
import { getCNPDFUpdateObjectCount } from "selectors/documentSelectors";

let cnpdfLoggingManager = null;

class CnpdfLoggingManager {
  constructor() {
    this.MAX_SYNC_FETCHED_COMPONENTS_TO_LOG = 5;
    this._preFetchedComponentLogs = [];
    this._syncFetchedComponentLogs = [];
    this._numSyncFetchedComponentsLogged = 0;
    this._componentsFetched = 0;
    this._syncFetchedComponents = 0;
    this._componentsFetchedBeforeInitialPageLoad = 0;
    this._syncFetchedComponentsBeforeInitialPageLoad = 0;
    this._syncFetchedComponentBucket = {
      [cnpdfComponentFetchTime.UNDER_200MS]: 0,
      [cnpdfComponentFetchTime.UNDER_500MS]: 0,
      [cnpdfComponentFetchTime.UNDER_1000MS]: 0,
      [cnpdfComponentFetchTime.OVER_1000MS]: 0
    };
  }

  init() {
    initialPageLoadedPromise(stores).then(() => {
      this._componentsFetchedBeforeInitialPageLoad = this._componentsFetched;
      this._syncFetchedComponentsBeforeInitialPageLoad = this._syncFetchedComponents;
    });
    docApi.isCompleteCNPDFAvailable().then(result => {
      if (result) {
        this._sendAggregateLogs();
      }
    });
  }

  _getBucket = syncFetchTime => {
    if (syncFetchTime < 200) {
      return cnpdfComponentFetchTime.UNDER_200MS;
    } else if (syncFetchTime < 500) {
      return cnpdfComponentFetchTime.UNDER_500MS;
    } else if (syncFetchTime < 1000) {
      return cnpdfComponentFetchTime.UNDER_1000MS;
    }
    return cnpdfComponentFetchTime.OVER_1000MS;
  };

  _sendAggregateLogs = async () => {
    const shareUserType = await getSharedFileUserType();
    logCNPDFComponentLogs("cnpdfAggregateLogs", {
      cnpdfDownloadTime: this._getCnpdfDownloadTotalTime(),
      updateObjectCount: getCNPDFUpdateObjectCount(stores),
      cnpdfSwitchBegin: getCNPDFSwitchBeginTime(),
      pdfToCnpdfSwitchTime: getPDFToCNPDFSwitchTime(),
      fetchedBeforeInitialPageLoad: this._componentsFetchedBeforeInitialPageLoad,
      fetchedComponents: this._componentsFetched,
      syncedBeforeInitialPageLoad: this._syncFetchedComponentsBeforeInitialPageLoad,
      syncedComponents: this._syncFetchedComponents,
      syncedOver1000ms: this._syncFetchedComponentBucket[cnpdfComponentFetchTime.OVER_1000MS],
      syncedUnder200ms: this._syncFetchedComponentBucket[cnpdfComponentFetchTime.UNDER_200MS],
      syncedUnder500ms: this._syncFetchedComponentBucket[cnpdfComponentFetchTime.UNDER_500MS],
      syncedUnder1000ms: this._syncFetchedComponentBucket[cnpdfComponentFetchTime.UNDER_1000MS],
      shareUserType
    });
  };

  _getPerformanceMarker = markerName => {
    const entries = performance.getEntriesByName(markerName);
    return entries.length ? entries[0].startTime : 0;
  };

  _getDownloadTime = item => {
    const downloadTimeByPath =
      this._getPerformanceMarker(`${item.componentPath}_downloadEnd`) -
      this._getPerformanceMarker(`${item.componentPath}_downloadStart`);
    if (downloadTimeByPath) {
      return downloadTimeByPath;
    }

    return (
      this._getPerformanceMarker(`${item.componentId}_downloadEnd`) -
      this._getPerformanceMarker(`${item.componentId}_downloadStart`)
    );
  };

  _getCnpdfDownloadTotalTime() {
    const cnpdfDownloadStartTime = this._getPerformanceMarker("cnpdfDownloadStart");
    const cnpdfDownloadEndTime = this._getPerformanceMarker("cnpdfDownloadEnd");
    return cnpdfDownloadEndTime - cnpdfDownloadStartTime;
  }

  _logSyncFetchedComponents = async () => {
    if (this._numSyncFetchedComponentsLogged >= this.MAX_SYNC_FETCHED_COMPONENTS_TO_LOG) {
      return;
    }
    for (
      this._numSyncFetchedComponentsLogged;
      this._numSyncFetchedComponentsLogged < this.MAX_SYNC_FETCHED_COMPONENTS_TO_LOG &&
      this._syncFetchedComponentLogs[this._numSyncFetchedComponentsLogged];
      this._numSyncFetchedComponentsLogged++
    ) {
      const componentLog = this._syncFetchedComponentLogs[this._numSyncFetchedComponentsLogged];

      // Prefix every key with 'component'
      const modifiedLogs = Object.fromEntries(
        Object.entries(componentLog).map(([key, value]) => [`component${key}`, value])
      );

      const shareUserType = await getSharedFileUserType();
      logCNPDFComponentLogs("syncFetchedComponent", { ...modifiedLogs, shareUserType });
    }
  };

  _cacheLogs = componentLogs => {
    componentLogs.forEach(component => {
      if (component.syncFetchTime !== undefined) {
        this._syncFetchedComponentLogs.push(component);
        this._syncFetchedComponents++;
        const bucket = this._getBucket(component.syncFetchTime);
        this._syncFetchedComponentBucket[bucket] += 1;
      } else {
        this._preFetchedComponentLogs.push(component);
      }
      this._componentsFetched++;
    });
    this._logSyncFetchedComponents();
  };

  cacheComponentFetchLogs = data => {
    const transformed = data.map(log => {
      const downloadTime = this._getDownloadTime(log);
      return {
        path: log.componentPath,
        id: log.componentId,
        ...(log.timeTakenToSyncFetch !== undefined && { syncFetchTime: parseInt(log.timeTakenToSyncFetch) }),
        ...(log.timeTakenToPrefetch !== undefined && { prefetchTime: parseInt(log.timeTakenToPrefetch) }),
        ...(downloadTime !== undefined && { downloadTime: parseInt(downloadTime) }),
        trigger: log.trigger
      };
    });
    this._cacheLogs(transformed);
  };
}

const getCnpdfLoggingManager = () => {
  if (!cnpdfLoggingManager) {
    cnpdfLoggingManager = new CnpdfLoggingManager();
    cnpdfLoggingManager.init();
  }
  return cnpdfLoggingManager;
};

export default getCnpdfLoggingManager;
